import { type Accessor, type Component, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import { variantLabel } from '~/services/product.service'
import type { DraftVariantType } from '~/types/ProductForm'

export interface VariantesTabPropsType {
  attributes: { name: string; values: string[] }[]
  variants: DraftVariantType[]
  isMobile: Accessor<boolean>
  tariffNames: string[]
  editingTariff: Accessor<string>
  setEditingTariff: (name: string) => void
  hasMultipleTariffs: boolean
  pendingCombinations: () => Record<string, string>[]
  generateVariants: () => void
  openCreateAttribute: () => void
  openEditAttribute: (index: number) => void
  removeAttribute: (index: number) => void
  openEditVariant: (index: number) => void
  removeVariant: (index: number) => void
}

/** Pestaña Variantes: atributos, generación de combinaciones y variantes. */
const VariantesTab: Component<VariantesTabPropsType> = props => {
  return (
    <div class="space-y-5">
      <div>
        <div class="flex items-center justify-between mb-2">
          <div>
            <h3 class="font-semibold text-foreground">Atributos</h3>
            <p class="text-xs text-foreground-subtle">En qué se diferencian: talla, color, aroma…</p>
          </div>
          <Button size="sm" variant="soft" color="default" onClick={props.openCreateAttribute} prependIcon="material-symbols-light:add">
            Añadir atributo
          </Button>
        </div>

        <div class="space-y-2">
          <For each={props.attributes}>
            {(attribute, index) => (
              <SwipeAction
                itemId={`attr-${index()}`}
                rememberPosition
                onClick={() => props.openEditAttribute(index())}
                leftOptions={[{ icon: 'material-symbols-light:edit-outline', action: () => props.openEditAttribute(index()), color: 'warning', label: 'Editar' }]}
                rightOptions={[{ icon: 'material-symbols-light:delete-outline', action: () => props.removeAttribute(index()), color: 'danger', label: 'Eliminar' }]}
              >
                <div class="rounded-lg border border-border p-3 bg-surface">
                  <p class="font-medium text-foreground truncate">{attribute.name || 'Atributo sin nombre'}</p>
                  <div class="flex flex-wrap gap-1.5 mt-2">
                    <For each={attribute.values}>
                      {value => (
                        <Chip color="default" variant="soft" size="xs">
                          {value}
                        </Chip>
                      )}
                    </For>
                    <Show when={attribute.values.length === 0}>
                      <span class="text-xs text-foreground-subtle">Sin valores definidos</span>
                    </Show>
                  </div>
                </div>
              </SwipeAction>
            )}
          </For>

          <Show when={props.attributes.length === 0}>
            <p class="text-sm text-foreground-subtle">Sin atributos, el producto se vende como uno solo.</p>
          </Show>
        </div>
      </div>

      <div>
        <div class="flex flex-wrap items-end justify-between gap-3 mb-2">
          <div>
            <h3 class="font-semibold text-foreground">Variantes</h3>
            <p class="text-xs text-foreground-subtle">Cada combinación tiene su SKU y sus existencias</p>
          </div>
          <div class="flex flex-wrap items-end gap-2">
            <Show when={props.hasMultipleTariffs}>
              <div class="w-44">
                <Select
                  label="Precios de la tarifa"
                  options={props.tariffNames.map(name => ({ value: name, title: name }))}
                  value={props.editingTariff()}
                  onChange={v => props.setEditingTariff(String(v ?? ''))}
                />
              </div>
            </Show>
            <Button size="sm" color="info" variant="soft" onClick={props.generateVariants} disabled={props.attributes.length === 0} prependIcon="material-symbols-light:star-shine-outline">
              Generar {props.pendingCombinations().length > 0 ? `(${props.pendingCombinations().length})` : ''}
            </Button>
          </div>
        </div>

        <Show when={props.variants.length > 0} fallback={<p class="text-sm text-foreground-subtle">Aún no hay variantes.</p>}>
          <Show when={props.isMobile()}>
            <div class="space-y-2">
              <For each={props.variants}>
                {(variant, index) => (
                  <SwipeAction
                    itemId={`variant-${index()}`}
                    rememberPosition
                    onClick={() => props.openEditVariant(index())}
                    leftOptions={[{ icon: 'material-symbols-light:edit-outline', action: () => props.openEditVariant(index()), color: 'warning', label: 'Editar' }]}
                    rightOptions={[{ icon: 'material-symbols-light:delete-outline', action: () => props.removeVariant(index()), color: 'danger', label: 'Eliminar' }]}
                  >
                    <div class="rounded-lg border border-border p-3 bg-surface">
                      <div class="flex items-center justify-between gap-2">
                        <p class="font-medium text-foreground truncate">{variantLabel(variant)}</p>
                        <Show when={variant.id != null}>
                          <span class="text-xs text-foreground-subtle shrink-0">{variant.stock.toLocaleString('es-CO')} en stock</span>
                        </Show>
                      </div>
                      <div class="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span class="text-xs text-foreground-subtle">SKU {variant.sku || '—'}</span>
                        <Show when={variant.overrides[props.editingTariff()] != null}>
                          <span class="text-sm font-semibold text-foreground">${Number(variant.overrides[props.editingTariff()]).toLocaleString('es-CO')}</span>
                        </Show>
                        <Show when={!variant.active}>
                          <Badge color="default" variant="soft" size="sm">
                            Inactiva
                          </Badge>
                        </Show>
                      </div>
                    </div>
                  </SwipeAction>
                )}
              </For>
            </div>
            <p class="text-xs text-foreground-subtle mt-2">Toca una variante para editar su SKU, precio y existencias.</p>
          </Show>

          <Show when={!props.isMobile()}>
            <Table<DraftVariantType>
              data={props.variants}
              dense
              keyField="sku"
              emptyMessage="Aún no hay variantes."
              columns={[
                { header: 'Variante', accessor: (v: DraftVariantType) => <span class="font-medium text-foreground">{variantLabel(v)}</span> },
                { header: 'SKU', accessor: (v: DraftVariantType) => <span class="text-foreground-subtle">{v.sku || '—'}</span> },
                {
                  header: 'Precio',
                  accessor: (v: DraftVariantType) =>
                    v.overrides[props.editingTariff()] != null ? (
                      <span class="text-foreground">${Number(v.overrides[props.editingTariff()]).toLocaleString('es-CO')}</span>
                    ) : (
                      <span class="text-foreground-subtle">—</span>
                    )
                },
                {
                  header: 'Existencias',
                  accessor: (v: DraftVariantType) => (v.id != null ? <span class="text-foreground-subtle">{v.stock.toLocaleString('es-CO')}</span> : <span class="text-foreground-subtle">—</span>)
                },
                {
                  header: 'Estado',
                  accessor: (v: DraftVariantType) =>
                    v.active ? (
                      <span class="text-success">Activa</span>
                    ) : (
                      <Badge color="default" variant="soft" size="sm">
                        Inactiva
                      </Badge>
                    )
                }
              ]}
              actions={v => (
                <>
                  <Button size="sm" variant="text" color="warning" icon="material-symbols-light:edit-outline" tooltip="Editar" onClick={() => props.openEditVariant(props.variants.indexOf(v))} />
                  <Button size="sm" variant="text" color="danger" icon="material-symbols-light:delete-outline" tooltip="Eliminar" onClick={() => props.removeVariant(props.variants.indexOf(v))} />
                </>
              )}
              pagination={false}
              pinnable={false}
            />
          </Show>
        </Show>
      </div>
    </div>
  )
}

export default VariantesTab
